import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import moment from 'moment';
import { debounce } from 'lodash';
import {
  Button,
  Select,
  MenuItem,
  FormControl,
  TextField,
  SelectChangeEvent,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LinkIcon from '@mui/icons-material/Link';
import toast from 'react-hot-toast';
import userProgramsService from '../../../services/userProgramsService';
import { STATUS_OPTIONS } from '../constants';

function isValidUrl(url: string): boolean {
  const trimmed = url.trim();
  return trimmed.startsWith('https://') || trimmed.startsWith('http://');
}

export interface ApplicationFormValues {
  status: string;
  targetDate: string;
  link: string;
  notes: string;
}

interface ApplicationFormDropdownProps {
  onClose?: () => void;
  onSubmit?: (values: ApplicationFormValues) => void;
  initialValues?: Partial<ApplicationFormValues>;
  /** ID from details.programManager.id — when set, Save will call PATCH /api/programs-manager/:id */
  programManagerId?: number;
}

const ApplicationFormDropdown: React.FC<ApplicationFormDropdownProps> = ({
  onClose,
  onSubmit,
  initialValues,
  programManagerId,
}) => {
  const [status, setStatus] = useState<string>(initialValues?.status ?? 'not_started');
  const [targetDate, setTargetDate] = useState<string>(initialValues?.targetDate ?? '');
  const [link, setLink] = useState<string>(initialValues?.link ?? '');
  const [notes, setNotes] = useState<string>(initialValues?.notes ?? '');
  const [saving, setSaving] = useState(false);
  const [autoSaved, setAutoSaved] = useState(false);

  const statusRef = useRef(status);
  const targetDateRef = useRef(targetDate);
  const linkRef = useRef(link);
  const notesRef = useRef(notes);
  statusRef.current = status;
  targetDateRef.current = targetDate;
  linkRef.current = link;
  notesRef.current = notes;

  const onSubmitRef = useRef(onSubmit);
  onSubmitRef.current = onSubmit;

  const isFirstRender = useRef(true);

  const debouncedAutoSave = useMemo(
    () =>
      debounce(() => {
        if (programManagerId == null) return;
        const l = linkRef.current?.trim() ?? '';
        if (l && !isValidUrl(l)) return;
        userProgramsService
          .patchProgramsManager(programManagerId, {
            applicationForm: {
              status: statusRef.current,
              targetDate: targetDateRef.current,
              link: l || undefined,
              notes: notesRef.current,
            },
          })
          .then(() => {
            setAutoSaved(true);
            toast.success('Application form saved.');
            onSubmitRef.current?.({
              status: statusRef.current,
              targetDate: targetDateRef.current,
              link: l,
              notes: notesRef.current,
            });
          })
          .catch(() => {});
      }, 800),
    [programManagerId]
  );

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setAutoSaved(false);
    debouncedAutoSave();
    return () => debouncedAutoSave.cancel();
  }, [status, targetDate, link, notes, debouncedAutoSave]);

  // Chỉ sync status khi status từ parent đổi — tránh reset link/notes/targetDate khi đổi Application Status ở card trên
  useEffect(() => {
    setStatus(initialValues?.status ?? 'not_started');
  }, [initialValues?.status]);

  useEffect(() => {
    setTargetDate(initialValues?.targetDate ?? '');
    setLink(initialValues?.link ?? '');
    setNotes(initialValues?.notes ?? '');
  }, [initialValues?.targetDate, initialValues?.link, initialValues?.notes]);

  const handleStatusChange = useCallback((event: SelectChangeEvent<string>) => {
    setStatus(event.target.value);
  }, []);

  const handleTargetDateChange = useCallback((val: moment.Moment | null) => {
    setTargetDate(val && moment(val).isValid() ? moment(val).format('YYYY-MM-DD') : '');
  }, []);

  const handleLinkChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLink(e.target.value);
  }, []);

  const handleNotesChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNotes(e.target.value);
  }, []);

  const handleSubmit = useCallback(async () => {
    const values: ApplicationFormValues = { status, targetDate, link, notes };

    if (targetDate.trim() && isNaN(new Date(targetDate.trim()).getTime())) {
      toast.error('Please enter a valid Target Date.');
      return;
    }

    if (link.trim() && !isValidUrl(link)) {
      toast.error('Please enter a valid link (must start with https:// or http://).');
      return;
    }

    if (programManagerId != null) {
      setSaving(true);
      try {
        await userProgramsService.patchProgramsManager(programManagerId, {
          applicationForm: {
            status: values.status,
            targetDate: values.targetDate,
            link: values.link || undefined,
            notes: values.notes,
          },
        });
        toast.success('Application form saved.');
        onSubmit?.(values);
        onClose?.();
      } catch {
        // Error toast is handled by http interceptor
      } finally {
        setSaving(false);
      }
    } else {
      onSubmit?.(values);
      onClose?.();
    }
  }, [status, targetDate, link, notes, programManagerId, onSubmit, onClose]);

  return (
    <div
      style={{
        backgroundColor: '#fff',
        border: '1px solid #e5e7eb',
        borderTop: 'none',
        borderRadius: '0 0 12px 12px',
        padding: '20px 24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        marginTop: '-12px',
        marginBottom: '12px',
      }}
    >
      {/* <div style={{ fontSize: '18px', fontWeight: 700, color: '#111827', marginBottom: '20px' }}>
        1. Application Form
      </div> */}

      {/* Status và Target Date nằm cạnh nhau */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
        <div>
          <label
            htmlFor="application-form-status"
            style={{
              display: 'block',
              color: '#111827',
              fontSize: '14px',
              fontWeight: 500,
              marginBottom: '8px',
              lineHeight: 1.5,
            }}
          >
            Status
          </label>
          <FormControl fullWidth size="medium" sx={{ display: 'block' }}>
            <Select
              id="application-form-status"
              value={status}
              onChange={handleStatusChange}
              IconComponent={KeyboardArrowDownIcon}
              displayEmpty
              sx={{
                borderRadius: '8px',
                width: '100%',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
              }}
              variant="outlined"
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        <div>
          <label
            htmlFor="application-form-target-date-dropdown"
            style={{
              display: 'block',
              color: '#111827',
              fontSize: '14px',
              fontWeight: 500,
              marginBottom: '8px',
              lineHeight: 1.5,
            }}
          >
            Target Date
          </label>
          <DatePicker
            value={targetDate?.trim() && moment(targetDate).isValid() ? moment(targetDate) : null}
            onChange={handleTargetDateChange}
            inputFormat="MM/DD/yyyy"
            renderInput={(params) => (
              <TextField
                {...params}
                id="application-form-target-date-dropdown"
                fullWidth
                size="small"
                placeholder="MM/DD/yyyy"
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
                }}
              />
            )}
          />
        </div>
      </div>

      {/* Link (below Status & Target Date) */}
      <div style={{ marginBottom: '20px' }}>
        <label
          htmlFor="application-form-link"
          style={{
            display: 'block',
            color: '#111827',
            fontSize: '14px',
            fontWeight: 500,
            marginBottom: '8px',
            lineHeight: 1.5,
          }}
        >
          Link
        </label>
        <TextField
          id="application-form-link"
          fullWidth
          size="small"
          value={link}
          onChange={handleLinkChange}
          placeholder="https://..."
          variant="outlined"
          hiddenLabel
          error={!!link?.trim() && !isValidUrl(link)}
          helperText={
            link?.trim() && !isValidUrl(link)
              ? 'Link must start with https:// or http://'
              : undefined
          }
          sx={{
            '& .MuiOutlinedInput-root': { borderRadius: '8px' },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
          }}
        />
        {link?.trim() && isValidUrl(link) && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
            <a
              href={link.trim()}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: '#2563eb',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                textDecoration: 'none',
              }}
            >
              <LinkIcon sx={{ fontSize: '18px' }} /> View Link
            </a>
          </div>
        )}
      </div>

      <div style={{ marginBottom: '20px' }}>
        <label
          htmlFor="application-form-notes"
          style={{
            display: 'block',
            color: '#111827',
            fontSize: '14px',
            fontWeight: 500,
            marginBottom: '8px',
            lineHeight: 1.5,
          }}
        >
          Notes
        </label>
        <TextField
          id="application-form-notes"
          fullWidth
          multiline
          minRows={4}
          placeholder="Add notes..."
          value={notes}
          onChange={handleNotesChange}
          variant="outlined"
          hiddenLabel
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              '& fieldset': { borderColor: '#e5e7eb' },
            },
          }}
        />
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginTop: '16px' }}>
        {programManagerId != null && autoSaved && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <CheckCircleIcon sx={{ fontSize: '18px', color: '#16a34a' }} />
            <span style={{ fontSize: '13px', color: '#6b7280' }}>Auto-saved</span>
          </div>
        )}
        <div style={{ display: 'flex', gap: '12px', marginLeft: 'auto' }}>
          <Button onClick={onClose} sx={{ color: '#6b7280', fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saving}
            sx={{ bgcolor: '#2563eb', fontWeight: 600 }}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ApplicationFormDropdown;
